import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, Share2, Sparkles, Target, Lightbulb, TrendingUp, MessageCircle } from "lucide-react";
import { Glow, SafeImage } from "@/components/site/bits";

const WA = "https://wa.me/917042141004";

export default function CaseStudy() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/portfolio/${id}`).then(({ data }) => setItem(data)).catch(() => setNotFound(true));
  }, [id]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: item?.name, url });
      else { await navigator.clipboard.writeText(url); toast.success("Case study link copied!"); }
    } catch (_) {}
  };

  if (notFound) return (
    <div className="min-h-screen grid place-items-center bg-[#0B1220] text-white grain">
      <div className="text-center">
        <p className="text-white/60">Case study not found.</p>
        <Link to="/" className="mt-4 inline-block rounded-full bg-[#00E5FF] text-[#0B1220] px-6 py-2.5 font-semibold">Back home</Link>
      </div>
    </div>
  );
  if (!item) return <div className="min-h-screen grid place-items-center bg-[#0B1220] text-white/50">Loading case study…</div>;

  const blocks = [
    { icon: Target, label: "The Problem", text: item.problem, color: "#7B61FF" },
    { icon: Lightbulb, label: "Our Solution", text: item.solution, color: "#00E5FF" },
    { icon: TrendingUp, label: "The Result", text: item.result, color: "#00FFB2" },
  ];

  return (
    <div className="relative min-h-screen bg-[#0B1220] text-white grain overflow-x-hidden" data-testid="case-study-page">
      <div className="relative grid-bg">
        <Glow className="w-[420px] h-[420px] -top-32 right-0" color="#7B61FF" />
        <div className="relative z-10 mx-auto max-w-5xl px-6 pt-10">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate(-1)} data-testid="case-back" className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-sm text-white/70 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <button onClick={share} data-testid="case-share" className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-sm text-white/70 hover:text-white">
              <Share2 className="h-4 w-4" /> Share
            </button>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mt-10">
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="rounded-full bg-[#00E5FF]/10 px-3 py-1 text-xs text-[#00E5FF]">{item.category}</span>
              <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-white/60">{item.industry}</span>
              {(item.tags || []).map((t) => <span key={t} className="rounded-full bg-white/5 px-3 py-1 text-xs text-white/60">{t}</span>)}
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tighter leading-[1.05]">{item.name}</h1>
            <p className="mt-4 max-w-2xl text-lg text-[#00FFB2]">{item.result}</p>
          </motion.div>

          {item.video ? (
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.1 }}
              className="mt-10 overflow-hidden rounded-3xl glass bg-black" data-testid="case-video">
              <video src={item.video} poster={item.image || undefined} controls playsInline className="w-full aspect-video object-contain bg-black" />
            </motion.div>
          ) : item.image ? (
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.1 }}
              className="mt-10 overflow-hidden rounded-3xl glass">
              <SafeImage src={item.image} alt={item.name} className="w-full aspect-[16/9] object-cover" fallbackClassName="w-full aspect-[16/9]" />
            </motion.div>
          ) : null}
        </div>
      </div>

      <div className="relative z-10 mx-auto max-w-5xl px-6 py-16 space-y-6">
        {blocks.map((b, i) => {
          const Icon = b.icon;
          return (
            <motion.div key={b.label} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.05 }}
              className="rounded-3xl glass p-8 md:p-10" data-testid={`case-block-${i}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: `${b.color}1a` }}>
                  <Icon className="h-5 w-5" style={{ color: b.color }} />
                </span>
                <span className="text-xs uppercase tracking-[0.25em] font-bold" style={{ color: b.color }}>{b.label}</span>
              </div>
              <p className="text-lg md:text-xl leading-relaxed text-white/85 font-display">{b.text}</p>
            </motion.div>
          );
        })}

        <div className="relative overflow-hidden rounded-3xl glass p-8 md:p-12 text-center">
          <Glow className="w-72 h-72 -top-20 left-1/2 -translate-x-1/2" color="#00E5FF" />
          <div className="relative">
            <Sparkles className="mx-auto h-8 w-8 text-[#00E5FF]" />
            <h3 className="mt-4 font-display text-2xl md:text-3xl font-bold">Want results like this for your business?</h3>
            <p className="mt-3 text-white/60 max-w-lg mx-auto">Let's build your AI-powered growth engine. Book a free consultation with our team.</p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link to="/#contact" data-testid="case-cta-contact" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#7B61FF] px-7 py-3.5 font-semibold text-[#0B1220] glow-cyan hover:scale-[1.03] transition-transform">
                Book Free Consultation <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a href={WA} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full glass px-6 py-3.5 font-semibold hover:border-[#00FFB2]/50 transition-colors">
                <MessageCircle className="h-4 w-4 text-[#00FFB2]" /> WhatsApp
              </a>
            </div>
          </div>
        </div>

        <div className="pt-4 text-center">
          <Link to="/#portfolio" className="text-sm text-white/50 hover:text-[#00E5FF] transition-colors">← View more work</Link>
        </div>
      </div>
    </div>
  );
}
